import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { CatalogItem } from '../../../../redux/api/fymAPI.types';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { getFullAPIFileURL } from '../../../common/getFullAPIFileURL';
import { useWatermarkChoice } from '../../watermarkUpsell/choice/useWatermarkChoice';
import { fileHasWatermark, thumbShapeOf } from '../../watermarkUpsell/files/watermarkedFile';
import { setPromoteMediaDraft } from '../draftStore';
import { inlineMediaUrl } from '../inlineMediaUrl';
import { studioThemeMessages } from '../studio/studioThemeMessages';
import { LibraryFile, libraryPostAspect } from './libraryGroups';

export interface PostLibraryFileOptions {
  /** Runs just before the Composer opens (the asset viewer closes itself here). */
  before?: () => void;
}

/** The row of the file: a kit asset or a reel. It says if the file has the watermark. */
function sourceRowOf(file: LibraryFile) {
  if (file.source === undefined) {
    return null;
  }
  return file.source.kind === 'kitAsset' ? file.source.asset : file.source.reel;
}

/**
 * "Post it" on a Library card: hands the file to the Composer through the draft store and opens
 * the Composer. Does nothing for a file the Composer cannot post (`libraryPostAspect` is null).
 * This is the one handover of "Post it" (the viewer, the Posts board, the Kit, the Work page), so
 * the choice of a watermarked file (design: trial-upsell a2) opens here, on each click, on Free and
 * the trial; "Post with the watermark" then opens the Composer with one click.
 */
export function usePostLibraryFile(): (item: CatalogItem, file: LibraryFile, options?: PostLibraryFileOptions) => void {
  const router = useSMLRouter();
  const intl = useIntl();
  const { gate } = useWatermarkChoice();

  return useCallback(
    (item: CatalogItem, file: LibraryFile, options?: PostLibraryFileOptions) => {
      const aspect = libraryPostAspect(file);
      const { fileId, durationSec } = file;
      if (aspect === null || fileId === null || durationSec === null) {
        return;
      }
      const row = sourceRowOf(file);
      const hasWatermark = fileHasWatermark(row);
      const handOver = () => {
        options?.before?.();
        setPromoteMediaDraft({
          fileId,
          fileUrl: getFullAPIFileURL(file.fileUrl),
          thumbnailUrl: file.posterUrl === null ? undefined : inlineMediaUrl(file.posterUrl),
          kind: 'video',
          aspect,
          width: file.width,
          height: file.height,
          durationSec,
          title: item.title,
          artistName: item.artistName,
          catalogItemUuid: item.uuid,
          smartLinkId: item.smartLinkId ?? undefined,
          releaseDate: item.releaseDate ?? undefined,
          renderedAt: file.madeAt,
          themeId: file.themeId,
          watermark: hasWatermark,
        });
        router.push(SMLRoutes.promoteComposer);
      };
      gate(hasWatermark, {
        surface: 'post',
        label: intl.formatMessage(studioThemeMessages[file.themeId].name),
        songTitle: item.title,
        thumbUrl: file.posterUrl === null ? null : inlineMediaUrl(file.posterUrl),
        shape: thumbShapeOf(file.width, file.height),
        width: file.width,
        height: file.height,
        preview: row?.preview === true,
        onContinue: handOver,
      });
    },
    [gate, intl, router]
  );
}
